import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Image } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { BaseDialog } from '@/components/shared/base-dialog';
import { ImageCropperDialog } from '@/components/shared/image-cropper/dialog';
import { ImageUploader } from '@/components/shared/image-uploader';
import {
  type ExternalResourceCoverImage,
  ResourceTypesLabels,
} from '@/models/resources';
import { cropImage } from '@/utils/crop-image';
import { defaultSchema } from './types';
import type {
  ActionsProps,
  CoverImageFieldProps,
  ExternalResourceDialogProps,
  FormData,
} from './types';

const Actions = ({ onSubmit, onClose }: ActionsProps) => (
  <Stack spacing={2} direction="row" justifyContent="flex-end">
    <Button onClick={onClose}>Cancelar</Button>
    <Button variant="contained" onClick={onSubmit}>
      Salvar
    </Button>
  </Stack>
);

const CoverImageField = ({
  coverImage,
  onUpload,
  onRemove,
}: CoverImageFieldProps) => {
  const previewSrc = coverImage?.previewUrl || coverImage?.url;

  if (!previewSrc) {
    return (
      <ImageUploader onUpload={onUpload}>
        <Button startIcon={<Image />} variant="outlined">
          Imagem da capa
        </Button>
      </ImageUploader>
    );
  }

  return (
    <Box
      p={2}
      sx={{ border: '1px solid', borderColor: 'primary.dark', borderRadius: 1 }}
      width="fit-content"
    >
      <Stack
        direction="row"
        spacing={1}
        mb={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Image fontSize="small" sx={{ color: 'primary.dark' }} />
          <Typography color="primary.dark" fontWeight="semibold">
            Imagem da capa
          </Typography>
        </Stack>
        <Button color="error" onClick={onRemove}>
          Remover
        </Button>
      </Stack>
      <Box
        component="img"
        src={previewSrc}
        alt="Imagem da capa"
        sx={{ maxWidth: 267, borderRadius: 1, display: 'block' }}
      />
    </Box>
  );
};

export const ExternalResourceDialog = ({
  onClose,
  onSubmit,
  customHeader,
  defaultValues,
  showCoverImage = false,
}: ExternalResourceDialogProps) => {
  const { control, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(defaultSchema),
    defaultValues: defaultValues ?? {
      name: '',
      url: '',
      category: ResourceTypesLabels.Course,
    },
  });

  const [coverImage, setCoverImage] =
    useState<ExternalResourceCoverImage | null>(
      defaultValues?.cover_image ?? null
    );
  const [rawUpload, setRawUpload] = useState<string>('');

  const handleUpload = (image: string | null) => {
    if (image) setRawUpload(image);
  };

  const handleFormSubmit = (data: FormData) => {
    onSubmit(showCoverImage ? { ...data, cover_image: coverImage } : data);
    onClose();
  };

  return (
    <BaseDialog
      title={customHeader ? customHeader : 'Adicionar recurso externo'}
      open
      fullWidth
      onClose={onClose}
      actionsComponent={
        <Actions onSubmit={handleSubmit(handleFormSubmit)} onClose={onClose} />
      }
    >
      <Stack spacing={2}>
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              <FormLabel htmlFor="name">Nome</FormLabel>
              <TextField
                {...field}
                error={!!error}
                id="name"
                placeholder="Qual é o nome do recurso?"
                variant="outlined"
                helperText={error?.message ?? ''}
                fullWidth
              />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="url"
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              <FormLabel htmlFor="url">URL</FormLabel>
              <TextField
                {...field}
                error={!!error}
                id="url"
                placeholder="Onde você encontrou?"
                variant="outlined"
                helperText={error?.message ?? ''}
                fullWidth
              />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="category"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormControl fullWidth>
              <FormLabel htmlFor="category">Tipo</FormLabel>
              <Autocomplete
                id="category"
                options={Object.values(ResourceTypesLabels).filter(
                  (label) => label !== ResourceTypesLabels.Others
                )}
                value={value || null}
                onChange={(_, newValue) => onChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!error}
                    helperText={error?.message}
                    placeholder="Selecione"
                    variant="outlined"
                  />
                )}
              />
            </FormControl>
          )}
        />
        {showCoverImage && (
          <FormControl fullWidth>
            <FormLabel>Imagem da capa</FormLabel>
            <CoverImageField
              coverImage={coverImage}
              onUpload={handleUpload}
              onRemove={() => setCoverImage(null)}
            />
          </FormControl>
        )}
      </Stack>
      {!!rawUpload && (
        <ImageCropperDialog
          image={rawUpload}
          onClose={() => setRawUpload('')}
          onCrop={async ({ cropArea, originalImage }) => {
            const previewUrl = await cropImage(originalImage, cropArea);
            setCoverImage({ blob: originalImage, cropArea, previewUrl });
          }}
          cropShape="rect"
          aspectRatio={2.5}
        />
      )}
    </BaseDialog>
  );
};
